import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { useSelector } from "react-redux";
import {
  HtmlTooltip,
  HtmlTooltipDisabled,
  StyledDarkRedInformationTypography,
} from "../../../components/commonComponents/StyledComponents/styled";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { DisabledMutedGreyButton } from "../../../components/commonComponents/Buttons/DisabledButton";
import { ListingCellCenter, ListingCellLeft, ListingCellRight, ListingHeaderContent } from "../../../components/commonComponents/TabularLists/styles";
import AddTenantUser from "../modalContent/AddTenantUser";
import { SortIcon } from "../../../api_calls/utils";
import {
  checkUserPermissions,
  checkUserDetailsFromCookie,
  deleteUser,
  manageUsersPaginationCookie,
  resetUserData,
  userListFilterCookieKey,
  userListStateKey,
} from "../../../api_calls/userManagement";
import { tenantRetailerScopeAllowsOperation } from "../../../api_calls/tenantRetailerRecordAccess";
import {
  getBottomNavRetailerSelectionFromCookies,
  isLockedOutsideAllRetailers,
  resolveRetailerSelectionOptions,
} from "../../../api_calls/retailerSelection";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { toast } from "react-toastify";
import { CustomTextInput } from "../../../components/commonComponents/TextInputs/style";
import Pagination from "../../../components/commonComponents/LoadMore/Pagination";
import { createCookie, deleteCookie } from "../../../api_calls/requestConfig";
import ListingDataTable from "../../../components/commonComponents/TabularLists/ListingDataTable";
import RetailerIconCount from "../../../components/commonComponents/TabularLists/RetailerIconCount";
import RolesByCompanyIcon from "../../../components/commonComponents/TabularLists/RolesByCompanyIcon";

const ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP = "Only in All Retailers";
const TENANT_LIST_TYPE = "tenant";

const TenantUserList = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalDetails, setModalDetails] = React.useState({});
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState({});
  const [searchVal, setSearchVal] = React.useState({ value: "" });
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  const checkUserDetails = checkUserDetailsFromCookie();
  const loggedInUserType = checkUserDetails.user_detail?.user_type;

  const stateKey = userListStateKey(TENANT_LIST_TYPE);
  const filterCookieKeyword = userListFilterCookieKey(TENANT_LIST_TYPE);

  const currentStateData = useSelector((state) => state.userSelections);

  const bottomNavRetailerSelection =
    typeof currentStateData.bottom_nav_selected_retailer !== "undefined"
      ? currentStateData.bottom_nav_selected_retailer
      : getBottomNavRetailerSelectionFromCookies();
  const isAccountManagementLocked = isLockedOutsideAllRetailers(
    bottomNavRetailerSelection,
    resolveRetailerSelectionOptions(currentStateData)
  );

  const [sortingMeasure, setSortingMeasure] = React.useState({
    username: "asc",
    email: "asc",
  });
  const [sortingParamName, setSortingParamName] = React.useState("username");

  const paginationStartValue =
    typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_start : 0;
  const paginationEndValue =
    typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_end : 10;

  const userRecords =
    typeof currentStateData[stateKey] !== "undefined" && currentStateData[stateKey].length !== 0
      ? currentStateData[stateKey]
      : [];

  const userRecordsLoading = typeof currentStateData[stateKey] === "undefined" && userRecords.length === 0
    ? true
    : false;

  const applyFilter = () => {
    if (userRecords.length === 0) {
      return [];
    }

    createCookie(filterCookieKeyword, JSON.stringify(searchVal.value));

    return sortedResults(
      userRecords.filter((user) => {
        const username = typeof user.username !== "undefined" && user.username !== null ? user.username : "";
        const email = typeof user.email !== "undefined" && user.email !== null ? user.email : "";
        const roles = Array.isArray(user.roles) ? user.roles : [];
        const assignedCompanyNames = (Array.isArray(user.assigned_companies) ? user.assigned_companies : [])
          .map((company) => company.name)
          .join(", ");
        const retailerNames = (Array.isArray(user.retailers) ? user.retailers : [])
          .map((retailer) => retailer.name)
          .join(", ");
        return (
          username.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
          email.toLowerCase().toString().includes(searchVal.value.toLowerCase().toString()) ||
          assignedCompanyNames.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
          retailerNames.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
          roles.join(",").toLowerCase().includes(searchVal.value.toLowerCase().toString())
        );
      })
    );
  };

  const sortedResults = (toBeSortedList) => {
    return toBeSortedList.slice().sort((a, b) => {
      const firstUsername = typeof a.username !== "undefined" && a.username !== null ? a.username : "";
      const secondUsername = typeof b.username !== "undefined" && b.username !== null ? b.username : "";
      const firstEmail = typeof a.email !== "undefined" && a.email !== null ? a.email : "";
      const secondEmail = typeof b.email !== "undefined" && b.email !== null ? b.email : "";

      if (sortingParamName === "email") {
        return sortingMeasure.email === "asc" ? firstEmail.localeCompare(secondEmail) : secondEmail.localeCompare(firstEmail);
      }

      return sortingMeasure.username === "asc"
        ? firstUsername.localeCompare(secondUsername)
        : secondUsername.localeCompare(firstUsername);
    });
  };

  const listItems = typeof searchVal.value !== "undefined" && searchVal.value !== "" ? applyFilter() : userRecords;
  const userListitems =
    listItems.length !== 0 ? sortedResults(listItems).slice(paginationStartValue, paginationEndValue) : [];

  const closeModal = () => {
    setModalDetails({});
    setIsModalOpen(false);
    resetUserData();
  };

  const openModal = (modalFor, userData) => {
    setModalDetails({
      openFor: modalFor,
      user_data: userData,
    });
    setIsModalOpen(true);
  };

  const editAction = (user) => {
    return (
      <HtmlTooltip title={<Typography variant="body4">Edit</Typography>}>
        <a
          href="javacript:void(0)"
          aria-label={"edit user for " + user.username}
          onClick={(e) => {
            e.preventDefault();
            openModal("update", user);
          }}
        >
          <Edit sx={{ color: "#AF3241" }} />
        </a>
      </HtmlTooltip>
    );
  };

  const deleteAction = (user) => {
    return (
      <HtmlTooltip title={<Typography variant="body4">Delete</Typography>}>
        <a
          href="javacript:void(0)"
          aria-label={"delete user for " + user.username}
          onClick={(e) => {
            e.preventDefault();
            setShowConfirmation(true);
            setUserToDelete(user);
          }}
        >
          <Delete sx={{ color: "#AF3241" }} />
        </a>
      </HtmlTooltip>
    );
  };

  const disabledEditAction = (user, tooltipTitle = "No Access") => {
    return (
      <HtmlTooltipDisabled title={tooltipTitle}>
        <a
          href="javacript:void(0)"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <Edit sx={{ color: "#E6E6E6" }} />
        </a>
      </HtmlTooltipDisabled>
    );
  };

  const disabledDeleteAction = (user, tooltipTitle = "No Access") => {
    return (
      <HtmlTooltipDisabled title={tooltipTitle}>
        <a
          href="javacript:void(0)"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <Delete sx={{ color: "#E6E6E6" }} />
        </a>
      </HtmlTooltipDisabled>
    );
  };

  const handleSortingParamChange = (paramName) => {
    setSortingMeasure((prevState) => {
      return {
        ...prevState,
        [paramName]: prevState[paramName] === "asc" ? "desc" : "asc",
      };
    });
    setSortingParamName(paramName);
  };

  const canEditTenantUser = permissionScope.user_list_can_edit_tenant === true;

  // Stays on the external delete flag until a tenant delete permission exists.
  const canDeleteTenantUser = permissionScope.user_list_can_delete_external === true;

  // user.retailers is the Retailer column. A tenant may edit or delete only when every name is assigned to them.
  const tenantMayOperateOnUser = (user) =>
    tenantRetailerScopeAllowsOperation(
      loggedInUserType,
      (Array.isArray(user.retailers) ? user.retailers : []).map((retailer) => retailer.name),
      currentStateData
    );

  const columns = React.useMemo(() => {
    return [
      {
        id: "username",
        header: (
          <ListingHeaderContent onClick={() => handleSortingParamChange("username")} style={{ cursor: "pointer" }}>
            Username <SortIcon direction={sortingMeasure.username} />
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellLeft>
            {user.username}
          </ListingCellLeft>
        ),
      },
      {
        id: "email",
        header: (
          <ListingHeaderContent onClick={() => handleSortingParamChange("email")} style={{ cursor: "pointer" }}>
            E-mail <SortIcon direction={sortingMeasure.email} />
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellLeft>
            {user.email}
          </ListingCellLeft>
        ),
      },
      {
        id: "retailer",
        headerAlign: "center",
        align: "center",
        header: (
          <ListingHeaderContent>
            Retailer
          </ListingHeaderContent>
        ),
        render: (user) => {
          const retailers = Array.isArray(user.retailers) ? user.retailers : [];
          return (
            <ListingCellCenter>
              <RetailerIconCount names={retailers.map((retailer) => retailer.name)} />
            </ListingCellCenter>
          );
        },
      },
      {
        id: "roles",
        headerAlign: "center",
        align: "center",
        header: (
          <ListingHeaderContent>
            Role
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellCenter>
            <RolesByCompanyIcon
              simplifyTooltipWhenUniform
              companies={Array.isArray(user.assigned_companies) ? user.assigned_companies : []}
            />
          </ListingCellCenter>
        ),
      },
      {
        id: "actions",
        headerAlign: "right",
        align: "right",
        header: (
          <ListingHeaderContent>
            Action
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellRight>
            {isAccountManagementLocked === true ? (
              <>
                {disabledEditAction(user, ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP)}
                {disabledDeleteAction(user, ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP)}
              </>
            ) : (
              <>
                {canEditTenantUser === true && tenantMayOperateOnUser(user) === true
                  ? editAction(user)
                  : disabledEditAction(user)}
                {canDeleteTenantUser === true && tenantMayOperateOnUser(user) === true
                  ? deleteAction(user)
                  : disabledDeleteAction(user)}
              </>
            )}
          </ListingCellRight>
        ),
      },
    ];
  }, [sortingMeasure, isAccountManagementLocked, canEditTenantUser, canDeleteTenantUser, loggedInUserType, currentStateData]);

  React.useEffect(() => {
    if (typeof document.cookie.split("; ").find((row) => row.startsWith(filterCookieKeyword)) !== "undefined") {
      const filterObj = JSON.parse(
        document.cookie
          .split("; ")
          .find((row) => row.startsWith(filterCookieKeyword))
          .split("=")[1]
      );
      setSearchVal({ value: filterObj });
    }

    manageUsersPaginationCookie({
      user_list_start: 0,
      user_list_end: 10,
    });
  }, [filterCookieKeyword]);

  const canCreateTenantUser = permissionScope.user_list_can_edit_tenant === true;

  return (
    <>
      {isAccountManagementLocked === true && (
        <>
          <StyledDarkRedInformationTypography variant="body2">
            Accounts with access to the currently selected retailer are displayed for reference only. Account
            management is only available in the “All Retailers” view.
          </StyledDarkRedInformationTypography>
          <Box height={20} />
        </>
      )}

      <Stack direction="row" spacing={2}>
        {canCreateTenantUser === true ? (
          isAccountManagementLocked === true ? (
            <DisabledMutedGreyButton
              onClick={(e) => {
                e.preventDefault();
              }}
              label="New Tenant User"
              icon={<Add />}
              toolTipContent={ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP}
              sx={{ mt: 3, mb: 2 }}
            />
          ) : (
            <ActionButton
              onClick={(e) => {
                e.preventDefault();
                openModal("add", {});
              }}
              label="New Tenant User"
              icon={<Add />}
            />
          )
        ) : (
          <></>
        )}
      </Stack>
      <Box height={30} />
      <Box width={350}>
        <CustomTextInput
          fullWidth
          variant="standard"
          placeholder="Search"
          label="Search"
          autoComplete="off"
          type={"text"}
          value={searchVal.value}
          onChange={(e) => {
            const nextSearchValue = e.target.value;
            setSearchVal({ value: nextSearchValue });
            if (nextSearchValue === "") {
              deleteCookie(filterCookieKeyword, true);
            }
            const pageSize =
              paginationEndValue > paginationStartValue
                ? paginationEndValue - paginationStartValue
                : 10;
            manageUsersPaginationCookie({
              user_list_start: 0,
              user_list_end: pageSize,
            });
          }}
          onBlur={(e) => { }}
        />
      </Box>

      <Box height={30} />
      <ListingDataTable
        columns={columns}
        rows={userListitems}
        rowKey={(user) => `${user.id}-${user.workspace_id}`}
        emptyText="No users found"
        loading={userRecordsLoading}
      />
      <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mt={2} mb={5}>
        <Pagination
          totalCount={listItems.length}
          reduxPaginationKeyword="users_pagination"
          reduxStartKeyword="user_list_start"
          reduxEndKeyword="user_list_end"
          useWindowScroll={true}
        />
      </Stack>

      {isModalOpen && (
        <AddTenantUser
          open={isModalOpen}
          onClose={closeModal}
          size={"md"}
          closeModal={closeModal}
          usedFor={modalDetails.openFor}
          userInfo={modalDetails.user_data}
        />
      )}
      {showConfirmation === true && (
        <Confirmation
          confirmationActionCall={() => {
            deleteUser(userToDelete, TENANT_LIST_TYPE).then((info) => {
              if (info.success === true) {
                setUserToDelete({});
                setShowConfirmation(false);
                toast("User deleted successfully ", { type: "success" });
              } else {
                toast("Sorry, something went wrong", { type: "error" });
              }
            });
          }}
          closePopup={(e) => {
            setUserToDelete({});
            setShowConfirmation(false);
          }}
          showConfirmationPopup={showConfirmation}
          confirmationTitle={"Delete User: " + userToDelete.username}
          confirmationMessage={
            <>
              <Typography variant="body2">
                You are deleting a user account. This action is permanent and cannot be reversed.
              </Typography>
              <Box height={20} />
              <Typography variant="body2">Are you sure you want to proceed?</Typography>
            </>
          }
        />
      )}
    </>
  );
};

export default TenantUserList;
